import { Link } from "react-router-dom";
import { MarketingPage } from "@/components/marketing/MarketingPage";
import {
  ShieldCheck, Lock, UserCheck, ScrollText, Server, KeyRound, FileText, AlertTriangle,
} from "lucide-react";

const PROTECTIONS = [
  { icon: KeyRound, title: "Role-based access (RBAC)", desc: "Students, advisors, administrators, and employers each see only the data their role permits. Employers see opt-in summaries only — never full records." },
  { icon: UserCheck, title: "Consent-first sharing", desc: "No student profile is ever visible to an employer without the student's explicit opt-in. Consent can be withdrawn at any time." },
  { icon: ScrollText, title: "Full audit trail", desc: "Every score-affecting action is logged with actor, timestamp, and reason. Employer 360 signals are labeled, individually weighted, and reversible — never silent." },
  { icon: Lock, title: "Least-privilege by design", desc: "Endpoints return the minimum fields required. Sensitive identifiers are never exposed to roles that don't need them." },
];

const THECB_REQS = [
  "FERPA governs all education records: GPA+ treats skill data, benchmarks, and coursework mappings as protected education records.",
  "Consistent with THECB policy, personal student information is not released to other parties without explicit written authorization or a permitted FERPA exception.",
  "Aligned with Texas Education Code § 61.031(d): information about current, former, or prospective students held to support access to postsecondary education is treated as confidential and released only in conformity with FERPA.",
  "Data-sharing with workforce or partner entities follows the model FERPA data-sharing framework contemplated by Texas Education Code § 7.012 (THECB, TEA, TWC).",
];

const SECURITY = [
  { t: "US-based storage", d: "Student data is stored within the United States." },
  { t: "Encryption in transit", d: "All external connections use TLS 1.2+; credentials are hashed, never stored in plain text." },
  { t: "Standards-aligned controls", d: "Architected toward NIST / SOC 2 Type 2, Texas TAC 202, and TX-RAMP expectations for state higher-ed data." },
  { t: "Breach response", d: "A defined incident process supports prompt notification to the institution's and THECB's security officers." },
];

export default function Privacy() {
  return (
    <MarketingPage
      eyebrow="Privacy & Compliance"
      title="Student data protection is the foundation of GPA+."
      lead="GPA+ is built FERPA-first and aligned with Texas Higher Education Coordinating Board (THECB) expectations for the confidentiality and secure handling of student information — because trust is a prerequisite, not a feature."
    >
      {/* How we protect */}
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-[#78be21]" />
        <h2 className="font-heading text-2xl font-bold text-[#003057]">How we protect student data</h2>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {PROTECTIONS.map((p) => (
          <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-6 card-elevate" data-testid="privacy-protection">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#003057]/6">
              <p.icon className="h-5 w-5 text-[#003057]" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-[#003057]">{p.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.desc}</p>
          </div>
        ))}
      </div>

      {/* THECB requirements */}
      <div className="mt-16 rounded-2xl border-t-4 border-[#005291] bg-slate-50 p-8">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-[#005291]" />
          <h2 className="font-heading text-2xl font-bold text-[#003057]">Texas (THECB) & FERPA requirements we honor</h2>
        </div>
        <ul className="mt-6 space-y-4">
          {THECB_REQS.map((r, i) => (
            <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700" data-testid="privacy-thecb-item">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#78be21]" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Security controls */}
      <div className="mt-16">
        <div className="flex items-center gap-3">
          <Server className="h-6 w-6 text-[#78be21]" />
          <h2 className="font-heading text-2xl font-bold text-[#003057]">Security controls</h2>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {SECURITY.map((s) => (
            <div key={s.t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-heading text-base font-bold text-[#003057]">{s.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FERPA rights */}
      <div className="mt-16 rounded-2xl bg-[#003057] p-8 text-white">
        <h2 className="font-heading text-2xl font-bold">Your FERPA rights as a student</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">
          Under FERPA, you have the right to inspect your education records, request corrections, and control
          the disclosure of personally identifiable information. In GPA+, that means you choose whether to share
          your profile with employers, you can withdraw that consent at any time, and any employer-contributed
          signal remains labeled and reversible.
        </p>
      </div>

      {/* Prototype note */}
      <div className="mt-10 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="privacy-prototype-note">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <p className="text-sm leading-relaxed text-amber-900">
          <span className="font-bold">Prototype notice:</span> This is a demonstration built on mock personas and
          research-derived sample data — it contains no real student personally identifiable information. The
          controls described here define how GPA+ is architected to operate on live institutional data.
        </p>
      </div>

      <div className="mt-10 text-center text-sm text-slate-500">
        Questions about data handling?{" "}
        <Link to="/faq" className="font-semibold text-[#5fa114] hover:underline">See our FAQ</Link>.
      </div>
    </MarketingPage>
  );
}
